import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Upload, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAttribution, trackLeadConversion } from "@/lib/tracking";
import { supabase } from "@/lib/supabase";

const ZONES = ["Cascais", "Oeiras", "Sintra", "Lisboa", "Outra"];

const TIMELINES = [
  "Só estou a explorar",
  "Nos próximos 3 meses",
  "Nos próximos 6 a 12 meses",
  "Quero avançar o quanto antes",
];

const INTENTS = [
  "Só ver uma ideia para o jardim",
  "Perceber ideia + faixa de investimento",
  "Falar com uma empresa se fizer sentido",
];

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    zone: "",
    timeline: "",
    intent: "",
    notes: "",
  });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("A imagem excede 8 MB. Tente outra fotografia.");
      return;
    }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.zone || !form.timeline || !form.intent) {
      toast.error("Preencha nome, telefone, zona, prazo e intenção.");
      return;
    }
    if (!consent) {
      toast.error("É necessário aceitar as condições de contacto para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      let photo_base64 = null;
      let photo_name = null;
      if (photo) {
        photo_base64 = await fileToBase64(photo);
        photo_name = photo.name;
      }
      const { error } = await supabase.from("leads").insert([{
        ...form,
        photo_base64,
        photo_name,
        ...getAttribution(),
      }]);
      if (error) throw error;
      trackLeadConversion(form);
      setDone(true);
      toast.success("Pedido recebido. Resposta em menos de 24 horas, mesmo ao fim de semana.");
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar. Tente novamente em instantes.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section
        id="pedido"
        data-testid="lead-form-success"
        className="bg-sand border-b border-rule"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-ink rounded-full mb-10">
            <Check size={22} strokeWidth={1.25} />
          </div>
          <div className="overline text-ink/60">Pedido registado</div>
          <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 tracking-tight leading-[1.05]">
            Obrigado, {form.name.split(" ")[0]}.
          </h2>
          <p className="mt-8 max-w-xl mx-auto text-base md:text-lg text-ink/70 font-light">
            A sua simulação será preparada em menos de 24 horas, mesmo se for fim de semana. Enviamos o resultado para {form.phone}. Se precisar, fale directamente connosco em hello@suapiscinapt.com.
          </p>
        </div>
      </section>
    );
  }

  const triggerCls =
    "input-edit !h-auto !rounded-none !border-0 !border-b !border-rule focus:!border-ink focus:!ring-0 !pl-0 !pr-0 !py-4 bg-transparent";

  return (
    <section
      id="pedido"
      data-testid="lead-form-section"
      className="bg-sand border-b border-rule"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <aside className="col-span-12 md:col-span-4 reveal">
            <div className="overline text-ink/60">Pedido de simulação</div>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05] text-ink">
              Um dossier
              <br />
              <span className="italic text-pool-deep">feito à mão.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-ink/70 font-light max-w-sm">
              Escreva como escreveria a um arquitecto. Pode deixar em branco o que não souber. Tratamos dos pormenores consigo depois.
            </p>

            <dl className="mt-12 space-y-6 border-t border-rule pt-8">
              <div className="flex justify-between items-baseline">
                <dt className="overline text-ink/50">Resposta</dt>
                <dd className="font-serif text-xl">{"<"} 24h</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt className="overline text-ink/50">Custo</dt>
                <dd className="font-serif text-xl">Sem custo</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt className="overline text-ink/50">Compromisso</dt>
                <dd className="font-serif text-xl italic">Nenhum</dd>
              </div>
            </dl>
          </aside>

          <form
            onSubmit={onSubmit}
            className="col-span-12 md:col-span-8 bg-paper border border-rule p-5 sm:p-8 md:p-12 reveal"
            data-testid="lead-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <div>
                <label className="overline text-ink/60">Nome</label>
                <input
                  data-testid="lead-field-name"
                  className="input-edit"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="O seu nome"
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="overline text-ink/60">Telefone / WhatsApp</label>
                <input
                  data-testid="lead-field-phone"
                  className="input-edit"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+351"
                  autoComplete="tel"
                />
              </div>
              <div>
                <label className="overline text-ink/60">Email (opcional)</label>
                <input
                  data-testid="lead-field-email"
                  type="email"
                  className="input-edit"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="email@exemplo.pt"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="overline text-ink/60">Zona</label>
                <Select value={form.zone} onValueChange={(v) => update("zone", v)}>
                  <SelectTrigger data-testid="lead-field-zone" className={triggerCls}>
                    <SelectValue placeholder="Selecione a zona" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {ZONES.map((z) => (
                      <SelectItem key={z} value={z} data-testid={`lead-zone-${z}`}>
                        {z}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-ink/60">Quando gostaria de avançar?</label>
                <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
                  <SelectTrigger data-testid="lead-field-timeline" className={triggerCls}>
                    <SelectValue placeholder="Selecione um prazo" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {TIMELINES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`lead-timeline-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="overline text-ink/60">O que procura neste momento?</label>
                <Select value={form.intent} onValueChange={(v) => update("intent", v)}>
                  <SelectTrigger data-testid="lead-field-intent" className={triggerCls}>
                    <SelectValue placeholder="Selecione uma intenção" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {INTENTS.map((i) => (
                      <SelectItem key={i} value={i} data-testid={`lead-intent-${i}`}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-10">
              <label className="overline text-ink/60">Nota (opcional)</label>
              <textarea
                data-testid="lead-field-notes"
                className="input-edit resize-none"
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Algo que devamos saber sobre o espaço ou as suas preferências"
              />
            </div>

            <div className="mt-10">
              <label className="overline text-ink/60">Fotografia do jardim</label>
              <label
                htmlFor="photo-upload"
                className="mt-3 flex items-center justify-between border border-dashed border-rule p-6 cursor-pointer hover:border-ink transition-colors"
                data-testid="lead-field-photo-label"
              >
                <div className="flex items-center gap-4">
                  <Upload size={18} strokeWidth={1.4} />
                  <span className="text-sm text-ink/70">
                    {photo ? photo.name : "Carregar imagem (JPG ou PNG, até 8 MB)"}
                  </span>
                </div>
                {preview && (
                  <img
                    src={preview}
                    alt="Pré-visualização"
                    className="w-14 h-14 object-cover"
                  />
                )}
              </label>
              <input
                id="photo-upload"
                data-testid="lead-field-photo"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
            </div>

            <div className="mt-10">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="lead-consent-label">
                <input
                  type="checkbox"
                  data-testid="lead-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 shrink-0 accent-ink cursor-pointer"
                />
                <span className="text-xs text-ink/60 leading-relaxed">
                  Autorizo o contacto por parte do Sua Piscina PT para preparação da simulação solicitada. Se decidir avançar, autorizo também que o meu contacto seja partilhado com um instalador parceiro adequado à minha zona.{" "}
                  <a href="/privacy" className="underline hover:text-ink">Política de Privacidade</a>.
                </span>
              </label>
            </div>

            <div className="mt-8 pt-8 border-t border-rule flex justify-end">
              <button
                type="submit"
                disabled={submitting || !consent}
                className="btn-primary disabled:opacity-40"
                data-testid="lead-submit"
              >
                {submitting ? "A enviar..." : "Enviar pedido"}
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
